import { Fragment } from 'react';

import { Alert, Box, CircularProgress, Grid } from '@mui/material';
import { useQuery } from 'react-query';
import CardServiceProviderDivider from 'src/components/CardServiceProviderDivider';
import { PanelLogin } from 'src/components/PanelLogin';
import Subtitle from 'src/components/Subtitle';
import { useAuthContext } from 'src/context/AuthContextProvider';
import { getDebenturesServiceProviders } from 'src/services/operation/operationDebentures';
import { DebenturesResumeData } from 'src/types/operations/debentures';

interface OperationDebenturesServiceProvidersProps {
  hashId: string;
}

type ServiceProvidersData = DebenturesResumeData['serviceProviders'];

const SECTIONS: { key: keyof ServiceProvidersData; label: string }[] = [
  { key: 'bancoMandatario', label: 'Banco Mandatário' },
  { key: 'agenteFiduciario', label: 'Agente Fiduciário' },
  { key: 'instituicaoDepositaria', label: 'Instituição Depositária' },
];

export default function OperationDebenturesServiceProviders({
  hashId,
}: OperationDebenturesServiceProvidersProps) {
  const { auth } = useAuthContext();

  const canViewServiceProviders = Boolean(
    auth?.user?.subscription?.plan?.permissions
      ?.operacoes_debentures_prestadores_de_servicos,
  );

  const { data, isError, isLoading } = useQuery({
    queryKey: ['debenturesServiceProviders', hashId],
    queryFn: () => getDebenturesServiceProviders(hashId),
    enabled: !!hashId && canViewServiceProviders,
    retry: false,
  });

  if (!canViewServiceProviders) {
    return (
      <Box p={2}>
        <PanelLogin />
      </Box>
    );
  }

  if (isLoading) {
    return (
      <Box p={2}>
        <CircularProgress color="primary" />
      </Box>
    );
  }

  if (isError) {
    return (
      <Box p={2}>
        <Alert severity="warning">
          Não foi possível carregar os prestadores de serviço desta debênture.
        </Alert>
      </Box>
    );
  }

  return (
    <Box p={2}>
      <Grid container spacing={3}>
        {SECTIONS.map(({ key, label }) => {
          const providers = data?.[key] ?? [];

          if (!providers.length) {
            return null;
          }

          return (
            <Fragment key={key}>
              <Grid item xs={12}>
                <Subtitle title={label} />
              </Grid>
              {providers.map((provider, index) => (
                <Grid
                  item
                  xs={12}
                  sm={6}
                  md={4}
                  key={provider.cnpj || provider.name || `${key}-${index}`}>
                  <CardServiceProviderDivider
                    serviceProvider={label}
                    name={provider.name}
                    logo={provider.logo}
                    slug={provider.slug ?? ''}
                    hashId={provider.hashId ?? ''}
                  />
                </Grid>
              ))}
            </Fragment>
          );
        })}
      </Grid>
    </Box>
  );
}
